/**
 * Wafer Interactive Module
 *
 * 晶圓裝飾元素的互動效果
 * 1. 滑動時更改漸層 (更改 .deco-chip__golden 的角度)
 * 2. 滑鼠移動時改變 transform: matrix
 */

import { windowStore } from '../stores/window-store.js';

/**
 * 線性插值函數
 */
function lerp(start, end, t) {
  return start + (end - start) * t;
}

/**
 * 全域動畫管理器 - 所有實例共用一個 RAF
 */
const animationManager = {
  instances: [],
  isRunning: false,
  rafId: null,
  lastMouseX: 0,
  lastMouseY: 0,

  add(instance) {
    this.instances.push(instance);
    if (!this.isRunning) {
      this.start();
    }
  },

  remove(instance) {
    const index = this.instances.indexOf(instance);
    if (index > -1) {
      this.instances.splice(index, 1);
    }
    if (this.instances.length === 0) {
      this.stop();
    }
  },

  start() {
    this.isRunning = true;
    this.tick();
  },

  stop() {
    this.isRunning = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  },

  tick() {
    if (!this.isRunning) return;

    // 更新所有實例
    for (let i = 0; i < this.instances.length; i++) {
      this.instances[i].update();
    }

    this.rafId = requestAnimationFrame(() => this.tick());
  },

  updateMousePosition(x, y) {
    this.lastMouseX = x;
    this.lastMouseY = y;
  },
};

// 全域滑鼠監聽（只綁定一次）
let isMouseListenerAdded = false;
function addGlobalMouseListener() {
  if (isMouseListenerAdded) return;
  isMouseListenerAdded = true;

  document.addEventListener(
    'mousemove',
    (event) => {
      animationManager.updateMousePosition(event.clientX, event.clientY);
    },
    { passive: true }
  );
}

// 全域陀螺儀監聽（手機/平板傾斜）
let isGyroListenerAdded = false;
function addGyroListener() {
  if (isGyroListenerAdded) return;
  if (!window.DeviceOrientationEvent) return;

  isGyroListenerAdded = true;

  // iOS 13+ 需要請求權限
  if (typeof DeviceOrientationEvent.requestPermission === 'function') {
    // 需要用戶互動觸發，在首次觸控時請求
    const requestPermission = () => {
      DeviceOrientationEvent.requestPermission()
        .then((response) => {
          if (response === 'granted') {
            enableGyro();
          }
        })
        .catch(console.error);
      document.removeEventListener('touchstart', requestPermission);
    };
    document.addEventListener('touchstart', requestPermission, { once: true });
  } else {
    // Android 或舊版 iOS
    enableGyro();
  }
}

// 陀螺儀靈敏度（相對於滑鼠，0.1 = 10%）
const GYRO_SENSITIVITY = 0.3;

function enableGyro() {
  window.addEventListener(
    'deviceorientation',
    (event) => {
      // beta: 前後傾斜 (-180 ~ 180)，gamma: 左右傾斜 (-90 ~ 90)
      const beta = event.beta || 0;
      const gamma = event.gamma || 0;

      // 將傾斜角度轉換為類似滑鼠位置的值
      // gamma: -90~90 → 映射到螢幕寬度
      // beta: -180~180 → 映射到螢幕高度（取 -45~45 範圍較自然）
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      // 限制範圍並映射（乘上靈敏度係數縮減晃動幅度）
      const clampedGamma = Math.max(-30, Math.min(30, gamma));
      const clampedBeta = Math.max(-30, Math.min(30, beta - 45)); // 減 45 讓手機平放時為中心

      const x = centerX + (clampedGamma / 30) * centerX * GYRO_SENSITIVITY;
      const y = centerY + (clampedBeta / 30) * centerY * GYRO_SENSITIVITY;

      animationManager.updateMousePosition(x, y);
    },
    { passive: true }
  );
}

class WaferInteractive {
  constructor(element) {
    this.element = element;

    // 取得設定
    this.amplitude = parseInt(element.dataset.amplitude || '30', 10);
    this.skewAmplitude = parseFloat(element.dataset.skewAmplitude || '0.05');
    this.gradientAmplitude = parseFloat(element.dataset.gradientAmplitude || '0'); // 漸層角度變化幅度
    this.svgGradientId = element.dataset.svgGradient || ''; // SVG 漸層 ID（例如 'paint2_linear'）

    // Parallax speed（支援響應式）
    this.parallaxSpeedDesktop = parseFloat(element.dataset.parallaxSpeed || '0');
    this.parallaxSpeedMobile = parseFloat(element.dataset.parallaxSpeedMobile ?? element.dataset.parallaxSpeed ?? '0');
    this.mobileBreakpoint = parseInt(element.dataset.mobileBreakpoint || '768', 10);
    this.parallaxSpeed = this.getParallaxSpeed();

    // 基礎 matrix 參數
    this.baseMatrix = {
      a: parseFloat(element.dataset.matrixA || '1'),
      b: parseFloat(element.dataset.matrixB || '0'),
      c: parseFloat(element.dataset.matrixC || '0'),
      d: parseFloat(element.dataset.matrixD || '1'),
    };

    // 狀態
    this.currentX = 0;
    this.currentY = 0;
    this.currentSkewB = 0;
    this.currentSkewC = 0;
    this.currentGradientOffset = 0;
    this.scrollY = 0;

    // 上一幀的值（避免重複設定相同值）
    this.lastTransform = '';
    this.lastGradientAngle = '';

    // 平滑係數
    this.smoothing = 0.08;

    // 啟用 GPU 加速
    this.element.style.willChange = 'transform';

    this.init();
  }

  getParallaxSpeed() {
    return window.innerWidth < this.mobileBreakpoint ? this.parallaxSpeedMobile : this.parallaxSpeedDesktop;
  }

  init() {
    // SVG 漸層會在 update 時延遲尋找（因為可能是動態載入）
    this.svgGradient = null;
    this.baseGradientCoords = null;

    // 監聽視窗大小變化，更新 parallaxSpeed
    this.handleResize = () => {
      this.parallaxSpeed = this.getParallaxSpeed();
    };
    window.addEventListener('resize', this.handleResize, { passive: true });

    // 訂閱滾動
    this.unsubscribeStore = windowStore.subscribe((state) => {
      this.scrollY = state.ws;
      this.updateGradient();
    });

    // 註冊到動畫管理器
    animationManager.add(this);

    // 確保全域監聯已啟動
    addGlobalMouseListener();
    addGyroListener();
  }

  updateGradient() {
    const baseAngle = 118 + this.scrollY * 0.15;
    const newAngle = (baseAngle + this.currentGradientOffset).toFixed(2);

    if (newAngle !== this.lastGradientAngle) {
      this.element.style.setProperty('--golden-angle', `${newAngle}deg`);
      this.lastGradientAngle = newAngle;

      // 延遲尋找 SVG 漸層元素（處理動態載入的情況）
      if (this.svgGradientId && !this.svgGradient) {
        this.svgGradient = this.element.querySelector(`#${this.svgGradientId}`);
        if (this.svgGradient) {
          // 儲存原始座標
          this.baseGradientCoords = {
            x1: parseFloat(this.svgGradient.getAttribute('x1')),
            y1: parseFloat(this.svgGradient.getAttribute('y1')),
            x2: parseFloat(this.svgGradient.getAttribute('x2')),
            y2: parseFloat(this.svgGradient.getAttribute('y2')),
          };
        }
      }

      // 更新 SVG 漸層座標
      if (this.svgGradient && this.baseGradientCoords) {
        this.updateSvgGradient(parseFloat(newAngle));
      }
    }
  }

  updateSvgGradient(angleDeg) {
    // CSS linear-gradient 和 SVG linearGradient 角度差 90 度
    // CSS: 0deg = 向上, 90deg = 向右
    // SVG: 0deg = 向右, 90deg = 向下
    const svgAngle = angleDeg - 90;
    const angleRad = (svgAngle * Math.PI) / 180;

    // 計算漸層中心點
    const base = this.baseGradientCoords;
    const cx = (base.x1 + base.x2) / 2;
    const cy = (base.y1 + base.y2) / 2;

    // 計算漸層長度的一半
    const halfLength = Math.sqrt(Math.pow(base.x2 - base.x1, 2) + Math.pow(base.y2 - base.y1, 2)) / 2;

    // 根據角度計算新座標
    const dx = Math.cos(angleRad) * halfLength;
    const dy = Math.sin(angleRad) * halfLength;

    const x1 = (cx - dx).toFixed(3);
    const y1 = (cy - dy).toFixed(3);
    const x2 = (cx + dx).toFixed(3);
    const y2 = (cy + dy).toFixed(3);

    this.svgGradient.setAttribute('x1', x1);
    this.svgGradient.setAttribute('y1', y1);
    this.svgGradient.setAttribute('x2', x2);
    this.svgGradient.setAttribute('y2', y2);
  }

  update() {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    // 從全域管理器取得滑鼠位置
    const normX = (animationManager.lastMouseX - centerX) / centerX;
    const normY = (animationManager.lastMouseY - centerY) / centerY;

    // 目標值
    const targetX = normX * this.amplitude;
    const targetY = normY * this.amplitude;
    const targetSkewB = normX * this.skewAmplitude;
    const targetSkewC = normY * this.skewAmplitude;
    const targetGradientOffset = normX * this.gradientAmplitude; // 漸層隨左右傾斜變化

    // 平滑插值
    this.currentX = lerp(this.currentX, targetX, this.smoothing);
    this.currentY = lerp(this.currentY, targetY, this.smoothing);
    this.currentSkewB = lerp(this.currentSkewB, targetSkewB, this.smoothing);
    this.currentSkewC = lerp(this.currentSkewC, targetSkewC, this.smoothing);
    this.currentGradientOffset = lerp(this.currentGradientOffset, targetGradientOffset, this.smoothing);

    // 計算 matrix
    const a = this.baseMatrix.a;
    const b = (this.baseMatrix.b + this.currentSkewB).toFixed(4);
    const c = (this.baseMatrix.c + this.currentSkewC).toFixed(4);
    const d = this.baseMatrix.d;
    const tx = this.currentX.toFixed(2);
    const ty = (this.currentY + this.scrollY * this.parallaxSpeed).toFixed(2);

    const transform = `matrix(${a}, ${b}, ${c}, ${d}, ${tx}, ${ty})`;

    // 只在值改變時更新 DOM
    if (transform !== this.lastTransform) {
      this.element.style.transform = transform;
      this.lastTransform = transform;
    }

    // 更新漸層角度
    if (this.gradientAmplitude !== 0) {
      this.updateGradient();
    }
  }

  destroy() {
    animationManager.remove(this);
    if (this.unsubscribeStore) this.unsubscribeStore();
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
    this.element.style.willChange = '';
  }
}

/**
 * 初始化函式
 */
export function initWaferInteractive() {
  const elements = document.querySelectorAll('[data-parallax]');
  const instances = [];

  elements.forEach((el) => {
    instances.push(new WaferInteractive(el));
  });

  return instances;
}

export default WaferInteractive;
